(ns ycweb.app.website.auth.common
  (:require
   [cemerick.url :as url]
   [rum.core :as rum]
   [ycweb.components.buttons :as buttons]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.components.grid :as grid]
   [ycweb.components.headings :as headings]
   [ycweb.util.core :as u]
   [ycweb.util.shapes :as s]
   [ycweb.util.urlstate :as urlstate]))

(defn redirect-params
  "The login page's afterloginredirect, for links to the register and forgot
   password screens, so e.g. a saved cart's owner who has no password yet still
   comes back to the cart. nil when there's none."
  [app-atom]
  (when-let [after-login-redirect (u/presence (:afterloginredirect (cmn/screen-params app-atom)))]
    {:afterloginredirect after-login-redirect}))

(defn yc-input-get-form-values [form-atom]
  (reduce
    (fn [acc [field-key field-data]]
      (assoc acc field-key
        (:value field-data)))
    {} @form-atom))

(defn display-input-errors [value-atom errors update-key]
  (swap! value-atom update update-key
    (fn [register]
      (reduce
        (fn [acc [field-key error-list]]
          (let [input-field (get acc field-key)]
            (cond
              (and
                (not (empty? error-list))
                (or
                  (nil? input-field)
                  (map? input-field)))
              (assoc-in acc [field-key :error-msg] (first error-list))

              (and
                (empty? error-list)
                (u/presence (:error-msg input-field)))
              (update acc field-key dissoc :error-msg)

              :else
              acc)))
        register errors))))

(defn validate-firstname [firstname errors]
  (let [firstname-value firstname
        current-errors
        (remove nil?
          [(when
            (and
              (u/presence firstname-value)
              (or
                (u/contains-html? firstname-value)
                (> (count firstname-value) 191)))
             "Please enter a valid first name")])]
    (assoc errors :user/firstname current-errors)))

(defn validate-lastname [lastname errors]
  (let [current-errors
        (remove nil?
          [(when
            (and
              (u/presence lastname)
              (or
                (u/contains-html? lastname)
                (> (count lastname) 191)))
             "Please enter a valid last name")])]
    (assoc errors :user/lastname current-errors)))

(defn validate-email [email errors]
  (let [clean-mail (s/clean-email (str email))
        current-errors
        (remove nil?
          [(cond
             (not (u/presence email))
             "Please enter your email address"

             (not (re-matches s/email-validate clean-mail))
             "Please enter a valid email address")])]
    (assoc errors :user/email current-errors)))

(defn validate-password [password password-confirmation password-length errors]
  (let [current-errors
        (remove nil?
          [(cond
             (not (u/presence password))
             "Please enter a password"

             (> 8 password-length)
             "A password must be at least 8 characters long"

             (not= password password-confirmation)
             "Your password doesn't match the confirmation")])

        confirmation-errors
        (remove nil?
          [(cond
             (not (u/presence password-confirmation))
             "Please enter a password")])]
    (assoc errors
      :user/password current-errors
      :user/password-confirmation confirmation-errors)))

(defn validate-plain-text-password [password password-confirmation errors]
  (let [current-errors
        (remove nil?
          [(cond
             (not (u/presence password))
             "Please enter a password"

             (> 8 (count password))
             "A password must be at least 8 characters long"

             (not= password password-confirmation)
             "Your password doesn't match the confirmation")])

        confirmation-errors
        (remove nil?
          [(cond
             (not (u/presence password-confirmation))
             "Please enter a password")])]
    (assoc errors
      :user/password current-errors
      :user/password-confirmation confirmation-errors)))

(rum/defc generic-loading-screen []
  [:main {:id "main-content"}
   [:div {:class (u/bem :yc-page-content [:default])}
    [:div {:class (u/bem :yc-section [:large])}
     [:div {:class "loading-panel"}
      [:i.u-animate-spin {:class (u/bem :icon [:block :large :is-dark :loading])}]
      [:div {:class "u-margin-top-16"}
       [:b {:class (u/bem :loading-text [:is-absolute])} "Loading"]]]]]])

(rum/defc auth-landing-page < rum/reactive [app-atom action]
  (let [user (cmn/curact app-atom :user)

        ;; Log out keeps where the login was meant to go (e.g. someone else's
        ;; saved-cart link on a shared computer). Encoded twice: href-props
        ;; encodes once more, and both logoff paths decode it twice before the
        ;; login page reads it (SPA: clean-params + the logoff screen's
        ;; url-decode; PHP: $_GET + login.clj's url-decode of the payload)
        logoff-goto
        (if-let [after-login-redirect (u/presence (:afterloginredirect (cmn/screen-params app-atom)))]
          (str "/login.htm?afterloginredirect=" (url/url-encode (url/url-encode after-login-redirect)))
          "/login.htm")]
    [:main#main-content
     {:class (u/bem :yc-page-content [:standard])}
     (cond
       user
       [:div
        {:class (u/bem :yc-section [:standard])}
        [:div.u-block-center.u-max-width-480.u-margin-bottom-20
         (headings/heading "Account Login" {:type :h1 :size :large :center true})
         (feedback/feedback :warning
           "Already logged in"
           (str
             (when (:firstname user)
               (str
                 "You are logged in as "
                 (:firstname user) " "
                 (:lastname user)))
             " "
             (if (:firstname user)
               "w"
               "W")
             "ith the email address " (:email user)))
         [:div.flex-grid-group
          [:div
           {:class (u/bem :flex-grid-block [:m-1-2])}
           [:div.flex-grid-content
            (buttons/button-web "Manage account"
              (merge
                (urlstate/href-props app-atom "myaccount")
                {:style :primary
                 :size :default
                 :full true}))]]
          [:div
           {:class (u/bem :flex-grid-block [:m-1-2])}
           [:div.flex-grid-content
            (buttons/button-web "Log out"
              (merge
                (urlstate/href-props app-atom "login" "logoff" {:goto logoff-goto})
                {:style :secondary
                 :size :default
                 :full true}))]]]]]

       :else
       [:div
        {:class (u/bem :yc-section [:large])}
        (headings/heading "Account Login" {:type :h1 :size :large :center true})
        [:div.u-block.u-block-center.u-max-width-240
         [:div.login-graphic.u-ratio-1-1
          [:i.login-graphic__icon {:class (u/bem :icon [:block :is-dark :emoticon-happy])}]]
         (buttons/button-web "Log in"
           {:type :button
            :style :primary
            :size :default
            :full true
            :on-click
            #(grid/open-modal app-atom
               :web.login/login-start
               {:config
                {:size :small
                 :blur-bg? true}})})
         [:hr {:class (u/bem :u-hr [:or])}]
         (buttons/button-web "Create an account"
           (merge
             (cond
               (= action "register")
               {:on-click
                #(grid/open-modal app-atom
                   :web.login/registration-options
                   {:config
                    {:size :small
                     :blur-bg? true}})}

               :else
               (urlstate/href-props app-atom "login" "register" (redirect-params app-atom)))
             {:type :button
              :style :secondary
              :size :default
              :full true}))]])]))